<template>
  <div class="modal fade plot-billing-modal" data-bs-backdrop="static" data-bs-keyboard="false" tabindex="-1" role="dialog" aria-labelledby="plotBillingModalLabel" aria-hidden="true">
    <div class="modal-dialog modal-lg modal-dialog-centered" role="document">
      <div class="modal-content">
        <div class="modal-header bg-success text-white">
          <h5 class="modal-title fw-bold text-white" id="plotBillingModalLabel">
            <i class="mdi mdi-receipt me-2"></i>Create Bill - Land Rates
          </h5>
          <button type="button" class="btn-close btn-close-white" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>

        <div class="modal-body p-0" v-if="property">
          <!-- Plot Information Card -->
          <div class="bg-light border-bottom p-4">
            <div class="row">
              <div class="col-md-6">
                <div class="property-info">
                  <h6 class="fw-bold text-success mb-3">
                    <i class="mdi mdi-store me-2"></i>Plot Details
                  </h6>
                  <div class="info-row mb-2">
                    <span class="text-muted">Plot Number:</span>
                    <span class="fw-bold ms-2">{{ property.LRNumber || 'N/A' }}</span>
                  </div>
                  <div class="info-row mb-2">
                    <span class="text-muted">Zone/Market:</span>
                    <span class="fw-bold ms-2">{{ property.zone || 'N/A' }}</span>
                  </div>
                  <div class="info-row mb-2">
                    <span class="text-muted">Owner:</span>
                    <span class="fw-bold ms-2">{{ property.ownerNames || 'N/A' }}</span>
                  </div>
                  <div class="info-row">
                    <span class="text-muted">Location:</span>
                    <span class="fw-bold ms-2">{{ property.subCountyName || '' }} {{ property.wardName ? '- ' + property.wardName : '' }}</span>
                  </div>
                </div>
              </div>

              <div class="col-md-6">
                <div class="financial-info">
                  <h6 class="fw-bold text-success mb-3">
                    <i class="mdi mdi-currency-usd me-2"></i>Financial Summary
                  </h6>
                  <div class="info-row mb-2">
                    <span class="text-muted">Annual Rent:</span>
                    <span class="fw-bold text-success ms-2">KES {{ numberFormat(annualCharges) }}</span>
                  </div>
                  <div class="info-row mb-2">
                    <span class="text-muted">Arrears B/F:</span>
                    <span class="fw-bold text-danger ms-2">KES {{ numberFormat(outstandingArrears) }}</span>
                  </div>
                  <div class="info-row mb-2">
                    <span class="text-muted">Current Balance:</span>
                    <span class="fw-bold ms-2" :class="balanceClass">KES {{ numberFormat(currentBalance) }}</span>
                  </div>
                  <div class="info-row">
                    <span class="text-muted">Status:</span>
                    <span class="ms-2">
                      <span class="badge" :class="statusBadgeClass">{{ property.status || 'Active' }}</span>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Billing Form -->
          <div class="p-4">
            <div class="text-center mb-4">
              <div class="billing-icon mb-3">
                <i class="mdi mdi-receipt display-1 text-success opacity-25"></i>
              </div>
              <h5 class="fw-bold text-dark mb-2">Generate Invoice</h5>
              <p class="text-muted mb-0">Select billing details and enter the amount to invoice</p>
            </div>

            <form @submit.prevent="handleGenerateBill">
              <div class="row">
                <!-- Income Type Selection -->
                <div class="col-md-6">
                  <div class="form-group mb-3">
                    <label class="form-label fw-semibold">
                      Income Type <span class="text-danger">*</span>
                    </label>
                    <select
                        v-model="selectedIncomeTypeId"
                        @change="handleIncomeTypeChange"
                        class="form-select"
                        :class="{ 'is-invalid': errors.incomeType }"
                        required>
                      <option value="">Select Income Type</option>
                      <option v-for="(item, index) in incomeTypes" :key="index" :value="item.incomeTypeId">
                        {{ item.incomeTypeDescription }}
                      </option>
                    </select>
                    <div v-if="errors.incomeType" class="invalid-feedback">{{ errors.incomeType }}</div>
                  </div>
                </div>

                <!-- Fee Type Selection -->
                <div class="col-md-6">
                  <div class="form-group mb-3">
                    <label class="form-label fw-semibold">
                      Fee Type <span class="text-danger">*</span>
                    </label>
                    <select
                        v-model="selectedFeeTypeId"
                        @change="handleFeeTypeChange"
                        class="form-select"
                        :class="{ 'is-invalid': errors.feeType }"
                        :disabled="!selectedIncomeTypeId || loadingFees"
                        required>
                      <option value="">{{ loadingFees ? 'Loading...' : 'Select Fee Type' }}</option>
                      <option v-for="(item, index) in feesAndCharges" :key="index" :value="item.feeId">
                        {{ item.feeDescription }} - KES {{ numberFormat(item.unitFeeAmount || 0) }}
                      </option>
                    </select>
                    <div v-if="errors.feeType" class="invalid-feedback">{{ errors.feeType }}</div>
                    <small v-if="!selectedIncomeTypeId" class="text-muted">Select income type first</small>
                  </div>
                </div>

                <!-- Fiscal Year -->
                <div class="col-md-6">
                  <div class="form-group mb-3">
                    <label class="form-label fw-semibold">
                      Fiscal Year <span class="text-danger">*</span>
                    </label>
                    <select v-model="fiscalYear" class="form-select" required>
                      <option :value="currentYear">{{ currentYear }}</option>
                      <option :value="currentYear + 1">{{ currentYear + 1 }}</option>
                    </select>
                  </div>
                </div>

                <!-- Amount Input -->
                <div class="col-md-6">
                  <div class="form-group mb-3">
                    <label class="form-label fw-semibold">
                      Amount to Invoice (KES) <span class="text-danger">*</span>
                    </label>
                    <div class="input-group">
                      <span class="input-group-text">KES</span>
                      <input
                          v-model.number="invoiceAmount"
                          type="number"
                          step="0.01"
                          min="0"
                          class="form-control"
                          :class="{ 'is-invalid': errors.amount }"
                          placeholder="Enter amount"
                          required>
                    </div>
                    <div v-if="errors.amount" class="invalid-feedback d-block">{{ errors.amount }}</div>
                  </div>
                </div>

                <!-- Quick Amount Buttons -->
                <div class="col-12 mb-3">
                  <small class="text-muted me-2">Quick amounts:</small>
                  <button
                      v-if="currentBalance > 0"
                      type="button"
                      class="btn btn-outline-danger btn-sm me-2"
                      @click="setQuickAmount(currentBalance)">
                    Full Balance ({{ numberFormat(currentBalance) }})
                  </button>
                  <button
                      v-if="annualCharges > 0"
                      type="button"
                      class="btn btn-outline-success btn-sm me-2"
                      @click="setQuickAmount(annualCharges)">
                    Annual Rent ({{ numberFormat(annualCharges) }})
                  </button>
                  <button
                      v-if="outstandingArrears > 0"
                      type="button"
                      class="btn btn-outline-warning btn-sm me-2"
                      @click="setQuickAmount(outstandingArrears)">
                    Arrears B/F ({{ numberFormat(outstandingArrears) }})
                  </button>
                  <button
                      v-if="selectedFeeType && selectedFeeType.unitFeeAmount > 1"
                      type="button"
                      class="btn btn-outline-primary btn-sm"
                      @click="setQuickAmount(selectedFeeType.unitFeeAmount)">
                    Fee Amount ({{ numberFormat(selectedFeeType.unitFeeAmount) }})
                  </button>
                </div>

                <!-- Billing Summary -->
                <div class="col-12">
                  <div class="card bg-light border-0 mb-4" v-if="invoiceAmount > 0">
                    <div class="card-body">
                      <h6 class="fw-bold text-success mb-3">
                        <i class="mdi mdi-calculator me-2"></i>Billing Summary
                      </h6>
                      <div class="row">
                        <div class="col-md-6">
                          <div class="summary-item mb-2">
                            <span class="text-muted">Plot:</span>
                            <span class="fw-bold ms-2">{{ property.LRNumber }} - {{ property.zone || 'N/A' }}</span>
                          </div>
                          <div class="summary-item mb-2">
                            <span class="text-muted">Fee Type:</span>
                            <span class="fw-bold ms-2">{{ selectedFeeType?.feeDescription || 'Not selected' }}</span>
                          </div>
                          <div class="summary-item mb-2">
                            <span class="text-muted">Fiscal Year:</span>
                            <span class="fw-bold ms-2">{{ fiscalYear }}</span>
                          </div>
                        </div>
                        <div class="col-md-6">
                          <div class="summary-item mb-2">
                            <span class="text-muted">Current Balance:</span>
                            <span class="fw-bold text-danger ms-2">KES {{ numberFormat(currentBalance) }}</span>
                          </div>
                          <div class="summary-item mb-2">
                            <span class="text-muted">Invoice Amount:</span>
                            <span class="fw-bold text-primary ms-2 h6">KES {{ numberFormat(invoiceAmount) }}</span>
                          </div>
                          <div class="summary-item">
                            <span class="text-muted">After Payment:</span>
                            <span class="fw-bold ms-2" :class="balanceAfterPaymentClass">
                              KES {{ numberFormat(balanceAfterPayment) }}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Action Buttons -->
              <div class="d-flex gap-2">
                <button type="button" class="btn btn-outline-secondary flex-fill" data-bs-dismiss="modal">
                  <i class="mdi mdi-close me-1"></i>Cancel
                </button>
                <button
                    type="submit"
                    class="btn btn-success flex-fill"
                    :disabled="!isFormValid || isGenerating">
                  <span v-if="isGenerating">
                    <span class="spinner-border spinner-border-sm me-2" role="status"></span>
                    Generating...
                  </span>
                  <span v-else>
                    <i class="mdi mdi-receipt me-1"></i>Generate Invoice
                  </span>
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'LandBillingModal',
  props: {
    property: {
      type: Object,
      default: () => ({})
    },
    incomeTypes: {
      type: Array,
      default: () => []
    },
    feesAndCharges: {
      type: Array,
      default: () => []
    },
    loadingFees: {
      type: Boolean,
      default: false
    }
  },
  emits: ['generate-bill', 'income-type-changed'],
  data() {
    return {
      invoiceAmount: 0,
      selectedIncomeTypeId: '',
      selectedFeeTypeId: '',
      fiscalYear: new Date().getFullYear(),
      currentYear: new Date().getFullYear(),
      isGenerating: false,
      errors: {}
    }
  },
  computed: {
    // Get the full selected income type object
    selectedIncomeType() {
      if (!this.selectedIncomeTypeId || !this.incomeTypes.length) return null;
      return this.incomeTypes.find(item => item.incomeTypeId === this.selectedIncomeTypeId) || null;
    },

    // Get the full selected fee type object
    selectedFeeType() {
      if (!this.selectedFeeTypeId || !this.feesAndCharges.length) return null;
      return this.feesAndCharges.find(item => item.feeId === this.selectedFeeTypeId) || null;
    },

    // Helper computed properties for cleaner field access
    currentBalance() {
      return parseFloat(this.property?.currentBalance) || parseFloat(this.property?.outstandingArrears) || 0;
    },
    annualCharges() {
      return parseFloat(this.property?.annualCharges) || 0;
    },
    outstandingArrears() {
      return parseFloat(this.property?.outstandingArrears) || 0;
    },

    balanceClass() {
      if (!this.property) return 'text-muted';
      return this.currentBalance > 0 ? 'text-danger' : 'text-success';
    },

    statusBadgeClass() {
      if (!this.property) return 'bg-secondary';
      const status = this.property.status || 'Active';
      switch (status) {
        case 'Active': return 'bg-success';
        case 'Suspended': return 'bg-danger';
        case 'Vacant': return 'bg-secondary';
        case 'Transferred': return 'bg-info';
        default: return 'bg-secondary';
      }
    },

    // Balance after this invoice is paid
    balanceAfterPayment() {
      if (!this.property) return 0;
      return Math.max(0, this.currentBalance - this.invoiceAmount);
    },

    balanceAfterPaymentClass() {
      return this.balanceAfterPayment > 0 ? 'text-warning' : 'text-success';
    },

    isFormValid() {
      return this.property &&
          this.selectedIncomeTypeId &&
          this.selectedFeeTypeId &&
          this.invoiceAmount > 0 &&
          this.fiscalYear &&
          Object.keys(this.errors).length === 0;
    }
  },
  watch: {
    property: {
      handler(newVal) {
        if (newVal && this.invoiceAmount === 0) {
          // Priority: currentBalance > outstandingArrears > annualCharges
          if (this.currentBalance > 0) {
            this.invoiceAmount = this.currentBalance;
          } else if (this.outstandingArrears > 0) {
            this.invoiceAmount = this.outstandingArrears;
          } else if (this.annualCharges > 0) {
            this.invoiceAmount = this.annualCharges;
          }
        }
      },
      immediate: true,
      deep: true
    },

    invoiceAmount(newVal) {
      this.validateAmount(newVal);
    },

    incomeTypes: {
      handler(newVal) {
        if (newVal && newVal.length > 0 && !this.selectedIncomeTypeId) {
          // Try to find "Land Rate" income type first (case-insensitive)
          const landRateIncomeType = newVal.find(item =>
              item.incomeTypeDescription &&
              item.incomeTypeDescription.toLowerCase().includes('land rate')
          );

          if (landRateIncomeType) {
            this.selectedIncomeTypeId = landRateIncomeType.incomeTypeId;
            this.$nextTick(() => {
              this.handleIncomeTypeChange();
            });
          } else if (newVal.length === 1) {
            // Fallback: if only one option, select it
            this.selectedIncomeTypeId = newVal[0].incomeTypeId;
            this.$nextTick(() => {
              this.handleIncomeTypeChange();
            });
          }
        }
      },
      immediate: true
    },

    feesAndCharges: {
      handler(newVal) {
        if (newVal && newVal.length > 0 && !this.selectedFeeTypeId) {
          // Try to find "Land Rate Fee" fee type first (case-insensitive)
          const landRateFee = newVal.find(item =>
              item.feeDescription &&
              item.feeDescription.toLowerCase().includes('land rate fee')
          );

          if (landRateFee) {
            this.selectedFeeTypeId = landRateFee.feeId;
            // Auto-populate amount if fee has a unit amount and invoice amount is still 0
            if (landRateFee.unitFeeAmount > 1 && this.invoiceAmount === 0) {
              this.invoiceAmount = parseFloat(landRateFee.unitFeeAmount);
            }
          } else {
            // Fallback: try to find any fee with "land rate" in the description
            const anyLandRateFee = newVal.find(item =>
                item.feeDescription &&
                item.feeDescription.toLowerCase().includes('land rate')
            );

            if (anyLandRateFee) {
              this.selectedFeeTypeId = anyLandRateFee.feeId;
              if (anyLandRateFee.unitFeeAmount > 1 && this.invoiceAmount === 0) {
                this.invoiceAmount = parseFloat(anyLandRateFee.unitFeeAmount);
              }
            } else if (newVal.length === 1) {
              // Fallback: if only one option, select it
              this.selectedFeeTypeId = newVal[0].feeId;
            }
          }
        }
      },
      immediate: true
    }
  },
  methods: {
    handleIncomeTypeChange() {
      this.selectedFeeTypeId = '';
      if (this.selectedIncomeType) {
        this.$emit('income-type-changed', this.selectedIncomeType);
      }
    },

    handleFeeTypeChange() {
      if (this.selectedFeeType?.unitFeeAmount > 1 && this.invoiceAmount === 0) {
        this.invoiceAmount = parseFloat(this.selectedFeeType.unitFeeAmount);
      }
    },

    setQuickAmount(amount) {
      this.invoiceAmount = parseFloat(amount) || 0;
    },

    validateAmount(amount) {
      delete this.errors.amount;

      if (!amount || amount <= 0) {
        this.errors.amount = 'Amount must be greater than 0';
      } else if (amount > 10000000) {
        this.errors.amount = 'Amount seems too large. Please verify.';
      }
    },

    validateForm() {
      this.errors = {};

      if (!this.selectedIncomeTypeId) {
        this.errors.incomeType = 'Please select an income type';
      }

      if (!this.selectedFeeTypeId) {
        this.errors.feeType = 'Please select a fee type';
      }

      this.validateAmount(this.invoiceAmount);

      return Object.keys(this.errors).length === 0;
    },

    handleGenerateBill() {
      if (!this.validateForm()) {
        return;
      }

      this.isGenerating = true;

      // Use correct field names from property object
      const billingData = {
        plotID: this.property.id,
        plotNumber: this.property.LRNumber,
        zone: this.property.zone,
        ownerNames: this.property.ownerNames,
        ownerID: this.property.ownerID || this.property.createdByIDNo,
        ownerPhoneNo: this.property.ownerPhoneNo,
        ownerEmail: this.property.ownerEmail || '',
        subCountyID: this.property.subCountyID,
        subCountyName: this.property.subCountyName,
        wardID: this.property.wardID,
        wardName: this.property.wardName,
        incomeTypeId: this.selectedIncomeType.incomeTypeId,
        incomeTypeDescription: this.selectedIncomeType.incomeTypeDescription,
        incomeTypePrefix: this.selectedIncomeType.incomeTypePrefix || 'LANDRATE',
        feeId: this.selectedFeeType.feeId,
        feeDescription: this.selectedFeeType.feeDescription,
        accountNo: this.selectedFeeType.accountNo || '',
        costCenterNo: this.selectedFeeType.costCenterNo || '',
        fiscalYear: this.fiscalYear,
        invoiceAmount: this.invoiceAmount,
        selectedIncomeType: this.selectedIncomeType,
        selectedFeeType: this.selectedFeeType,
        balanceBefore: this.currentBalance,
        annualRent: this.annualCharges,
        arrearsForward: this.outstandingArrears
      };

      this.$emit('generate-bill', billingData);
    },

    numberFormat(num) {
      return new Intl.NumberFormat("en-GB").format(num || 0);
    },

    resetForm() {
      this.invoiceAmount = 0;
      this.selectedIncomeTypeId = '';
      this.selectedFeeTypeId = '';
      this.fiscalYear = new Date().getFullYear();
      this.errors = {};
      this.isGenerating = false;
    },

    stopGenerating() {
      this.isGenerating = false;
    }
  }
}
</script>

<style scoped>
.info-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.summary-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.billing-icon {
  position: relative;
}

.property-info,
.financial-info {
  background: white;
  padding: 1rem;
  border-radius: 0.5rem;
  border: 1px solid #e9ecef;
  height: 100%;
}

.form-group {
  position: relative;
}

.input-group-text {
  background-color: #f8f9fa;
  border-right: none;
  font-weight: 600;
}

.form-control:focus {
  border-color: #28a745;
  box-shadow: 0 0 0 0.2rem rgba(40, 167, 69, 0.25);
}

.form-select:focus {
  border-color: #28a745;
  box-shadow: 0 0 0 0.2rem rgba(40, 167, 69, 0.25);
}

.btn-outline-danger:hover,
.btn-outline-success:hover,
.btn-outline-warning:hover,
.btn-outline-primary:hover {
  transform: translateY(-1px);
}

@media (max-width: 768px) {
  .info-row,
  .summary-item {
    flex-direction: column;
    align-items: flex-start;
  }

  .info-row .fw-bold,
  .summary-item .fw-bold {
    margin-left: 0 !important;
    margin-top: 0.25rem;
  }
}
</style>